/**
 * Ads do Mercado Livre — monitoramento do Product Ads das duas contas (pedido da Mariana, 09/10/2026).
 * Spec: entrega_dev_ads_ml/specs/2026-10-09-ads-ml-design.md.
 *
 * Obedece aos filtros do cabeçalho do BI (período e Canal → loja). KPIs no topo, farol por campanha SÓ pela
 * distância da meta, gasto da conta com mês contra mês, as duas conversões por anúncio e a Curva A sem Ads.
 * Tudo vem do retrato do Mongo (coleta das 10:40 ou do botão Atualizar) — nada chama a API do Meli na hora.
 * Só leitura: a tela não cria, pausa nem altera campanha.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Button, Card, CardContent, Grid, Stack, Tab, Tabs, TextField, Typography } from '@mui/material'
import { DomainPageShell } from '../components/DomainPageShell'
import { KpiCard } from '../components/KpiCard'
import { useRegisterPanelInsights } from '../state/PanelInsightsContext'
import { useBiData } from '../state/BiDataContext'
import { AbaAnuncios } from './ads/AbaAnuncios'
import { AbaCampanhas } from './ads/AbaCampanhas'
import { AbaCurvaA } from './ads/AbaCurvaA'
import { AbaGasto } from './ads/AbaGasto'
import { fetchAnuncios, fetchCampanhas, fetchColetaStatus, fetchCurvaA, fetchGastoDiario, fetchResumo, rodarColeta } from './ads/adsService'
import { subtituloFonte } from './ads/fonte'
import {
  aplicarMetaDaConta, brl, contaDoStoreKey, contarFarol, inteiro, intervaloDoPeriodo, lerMetas, metaAcosDoResumo, METAS_STORAGE, nomeConta, pct, UIDS,
  type AnuncioLinha, type CampanhaLinha, type ColetaStatus, type CurvaALinha, type GastoDiario, type MetasConta, type ResumoAds,
} from './ads/logic'

type Aba = 'campanhas' | 'gasto' | 'anuncios' | 'curva'
const ABAS: Array<[Aba, string]> = [['campanhas', 'Campanhas'], ['gasto', 'Gasto da conta'], ['anuncios', 'Anúncios'], ['curva', 'Curva A sem Ads']]

export function AdsPage() {
  const { periodQuery, storeKey, periodo } = useBiData()
  const janela = useMemo(() => intervaloDoPeriodo(periodQuery, new Date()), [periodQuery])
  const canal = contaDoStoreKey(storeKey)

  const [resumo, setResumo] = useState<ResumoAds | null>(null)
  const [coleta, setColeta] = useState<ColetaStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [aba, setAba] = useState<Aba>('campanhas')
  const [campanhas, setCampanhas] = useState<CampanhaLinha[] | null>(null)
  const [anuncios, setAnuncios] = useState<AnuncioLinha[] | null>(null)
  const [curva, setCurva] = useState<{ linhas: CurvaALinha[]; janela: { ini: string; fim: string } } | null>(null)
  const [gasto, setGasto] = useState<GastoDiario | null>(null)
  const [campanhaFiltro, setCampanhaFiltro] = useState<{ id: number; nome: string | null } | null>(null)
  const [abaLoading, setAbaLoading] = useState(false)
  const [avisoColeta, setAvisoColeta] = useState<string | null>(null)
  const pollRef = useRef<number | null>(null)
  // Metas da conta (pedido dela, 09/10/2026): ACOS alvo por conta e TACOS alvo, guardados no navegador como o
  // "Margem alvo" das Promoções. Campanha sem meta própria no Meli herda a da conta.
  const [metas, setMetas] = useState<MetasConta>(() => { try { return lerMetas(localStorage.getItem(METAS_STORAGE)) } catch { return lerMetas(null) } })
  const [metasTxt, setMetasTxt] = useState<Record<string, string>>(() => ({ ...Object.fromEntries(UIDS.map((u) => [u, metas.acos_alvo[u] == null ? '' : String(metas.acos_alvo[u])])), tacos: metas.tacos_alvo == null ? '' : String(metas.tacos_alvo) }))
  const aplicarMetas = useCallback(() => {
    const num = (t: string) => { const v = parseFloat((t || '').replace(',', '.')); return Number.isNaN(v) || v <= 0 || v >= 100 ? null : v }
    const next: MetasConta = { acos_alvo: Object.fromEntries(UIDS.map((u) => [u, num(metasTxt[u])])), tacos_alvo: num(metasTxt.tacos) }
    setMetas(next)
    try { localStorage.setItem(METAS_STORAGE, JSON.stringify(next)) } catch { /* sem storage */ }
  }, [metasTxt])
  const campanhasComMeta = useMemo(() => (campanhas ? aplicarMetaDaConta(campanhas, metas) : null), [campanhas, metas])

  const carregarBase = useCallback(async () => {
    if (canal === 'outro') { setLoading(false); setResumo(null); return }
    setLoading(true)
    setError(null)
    try {
      const [r, st] = await Promise.all([fetchResumo(janela.ini, janela.fim, storeKey), fetchColetaStatus().catch(() => null)])
      setResumo(r)
      setColeta(st)
      // as abas são recarregadas sob demanda: zera o que estava em memória
      setCampanhas(null); setAnuncios(null); setCurva(null); setGasto(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [canal, janela.ini, janela.fim, storeKey])

  useEffect(() => { void carregarBase() }, [carregarBase])

  // aba ativa carrega sob demanda (só a ativa é construída — regra da prévia de 18/09)
  useEffect(() => {
    if (canal === 'outro' || !resumo || resumo.vazio) return
    let cancel = false
    const run = async () => {
      setAbaLoading(true)
      try {
        if (aba === 'campanhas' && !campanhas) setCampanhas((await fetchCampanhas(janela.ini, janela.fim, storeKey)).campanhas)
        if (aba === 'anuncios' && !anuncios) setAnuncios((await fetchAnuncios(janela.ini, janela.fim, storeKey, campanhaFiltro?.id)).anuncios)
        if (aba === 'curva' && !curva) { const c = await fetchCurvaA(storeKey); if (!cancel) setCurva({ linhas: c.anuncios, janela: c.janela }) }
        if (aba === 'gasto' && !gasto) setGasto(await fetchGastoDiario(janela.ini, janela.fim, storeKey))
      } catch (e) {
        if (!cancel) setError(e instanceof Error ? e.message : String(e))
      } finally {
        if (!cancel) setAbaLoading(false)
      }
    }
    void run()
    return () => { cancel = true }
  }, [aba, resumo, campanhas, anuncios, curva, gasto, campanhaFiltro, canal, janela.ini, janela.fim, storeKey])

  const pararPoll = () => { if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null } }
  useEffect(() => pararPoll, [])

  const atualizar = useCallback(async () => {
    setAvisoColeta(null)
    try {
      const r = await rodarColeta()
      setColeta(r)
      if (!r.iniciado) { setAvisoColeta(r.motivo || r.erro || (r.ja_rodando ? 'coleta em andamento' : 'não iniciou')); if (!r.ja_rodando) return }
      pararPoll()
      pollRef.current = window.setInterval(async () => {
        const st = await fetchColetaStatus().catch(() => null)
        if (st) setColeta(st)
        if (st && !st.rodando) { pararPoll(); void carregarBase() }
      }, 10000)
    } catch (e) {
      setAvisoColeta(e instanceof Error ? e.message : String(e))
    }
  }, [carregarBase])

  const abrirAnuncios = useCallback((id: number) => {
    const nome = campanhas?.find((c) => c.campaign_id === id)?.nome ?? null
    setCampanhaFiltro({ id, nome })
    setAnuncios(null)
    setAba('anuncios')
  }, [campanhas])
  const limparCampanha = useCallback(() => { setCampanhaFiltro(null); setAnuncios(null) }, [])

  const faroL = useMemo(() => (campanhasComMeta ? contarFarol(campanhasComMeta) : null), [campanhasComMeta])
  // `sugestoes` entra nas dependências do efeito do hook: tem de ter identidade estável (array novo a cada render
  // = "Maximum update depth exceeded", visto na prévia de 09/10/2026).
  const sugestoes = useMemo(
    () =>
      faroL
        ? [
            ...(faroL.vermelho ? [{ titulo: `${faroL.vermelho} campanha(s) fora da meta`, detalhe: 'ACOS mais de 25% acima do alvo.', tom: 'critico' as const }] : []),
            ...(faroL.amarelo ? [{ titulo: `${faroL.amarelo} campanha(s) até 25% acima da meta`, detalhe: 'Vale acompanhar antes de mexer em verba.', tom: 'atencao' as const }] : []),
            ...(faroL.cinza ? [{ titulo: `${faroL.cinza} campanha(s) sem gasto ou ainda sem venda`, detalhe: 'Sem venda atribuída ainda não dá para medir contra a meta (campanha nova).', tom: 'neutro' as const }] : []),
          ]
        : undefined,
    [faroL],
  )
  useRegisterPanelInsights({
    titulo: 'Ads do Mercado Livre',
    verdade: 'Verdade = retrato da coleta de Product Ads (API do Meli) no Mongo + faturamento real da base margens. A tela é só leitura.',
    sugestoes,
  })

  const tot = resumo?.total ?? null
  const metaAcos = resumo && !resumo.vazio ? metaAcosDoResumo(resumo, metas) : null
  // identidade estável: o AbaGasto memoiza por esta lista (array novo a cada render derruba o Recharts)
  const contasIds = useMemo(() => (resumo ? Object.keys(resumo.contas) : []), [resumo])
  const subtitle = `${janela.ini} a ${janela.fim} · ${subtituloFonte(coleta, new Date())}`
  const rodando = Boolean(coleta?.rodando)

  const conteudoAba = () => {
    if (aba === 'campanhas') return campanhasComMeta ? <AbaCampanhas linhas={campanhasComMeta} onAbrirAnuncios={abrirAnuncios} /> : null
    if (aba === 'gasto') return gasto ? <AbaGasto gasto={gasto} contas={contasIds} /> : null
    if (aba === 'anuncios') return anuncios ? <AbaAnuncios linhas={anuncios} campanhaFiltro={campanhaFiltro} onLimparCampanha={limparCampanha} /> : null
    return curva ? <AbaCurvaA linhas={curva.linhas} janela={curva.janela} /> : null
  }

  return (
    <DomainPageShell title="Ads do Mercado Livre" subtitle={subtitle} loading={loading} error={error} onRetry={() => void carregarBase()}>
      <Stack spacing={2.5}>
        {canal === 'outro' ? (
          <Alert severity="info">Ads só existe no Mercado Livre. Escolha ML Matriz, ML Extrema ou tire o filtro de loja no cabeçalho.</Alert>
        ) : (
          <>
            <Card>
              <CardContent>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                    Período e loja vêm do cabeçalho do BI. Métricas até D-1 (o Meli consolida às 10h). Só leitura: nada aqui altera campanha.
                  </Typography>
                  <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
                    {avisoColeta ? <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{avisoColeta}</Typography> : null}
                    <Button size="small" variant="outlined" color="inherit" disabled={rodando} onClick={() => void atualizar()}
                      title="Dispara a coleta de Ads das duas contas em segundo plano (campanhas, anúncios e visitas; alguns minutos) e recarrega a tela ao terminar. Não escreve nada no Mercado Livre.">
                      {rodando ? `⏳ coleta em andamento${coleta?.iniciado_em ? ` desde ${new Date(coleta.iniciado_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}` : '↻ Atualizar (coletar agora)'}
                    </Button>
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Metas da conta (suas):</Typography>
                  {UIDS.map((u) => (
                    <TextField key={u} size="small" label={`ACOS alvo ${nomeConta(u)} %`} value={metasTxt[u] || ''} onChange={(e) => setMetasTxt((x) => ({ ...x, [u]: e.target.value }))} onBlur={aplicarMetas} onKeyDown={(e) => { if (e.key === 'Enter') aplicarMetas() }}
                      title="Vale para os KPIs do topo e para as campanhas que não têm ROAS alvo no Meli. Campanha com meta própria continua julgada pela dela. Padrão: ACOS 5 % (= ROAS 20); apagar o campo volta ao padrão." slotProps={{ htmlInput: { inputMode: 'decimal', style: { width: 110, textAlign: 'right' } } }} />
                  ))}
                  <TextField size="small" label="TACOS alvo %" value={metasTxt.tacos || ''} onChange={(e) => setMetasTxt((x) => ({ ...x, tacos: e.target.value }))} onBlur={aplicarMetas} onKeyDown={(e) => { if (e.key === 'Enter') aplicarMetas() }}
                    title="Gasto em Ads ÷ faturamento real (base margens), as duas contas juntas." slotProps={{ htmlInput: { inputMode: 'decimal', style: { width: 110, textAlign: 'right' } } }} />
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>padrão ACOS 5 % = ROAS 20 (saudável) · guardadas neste navegador · ROAS alvo = 100 ÷ ACOS alvo</Typography>
                </Stack>
                {janela.parcial ? <Alert severity="warning" sx={{ mt: 1.5, '& .MuiAlert-message': { fontSize: 13 } }}>O período inclui o dia de hoje{periodo === 'hoje' ? '' : ' (ou termina hoje)'}: o Meli consolida as métricas às 10h, então os números do dia corrente estão parciais.</Alert> : null}
                {coleta?.status?.ok === false ? <Alert severity="error" sx={{ mt: 1.5, '& .MuiAlert-message': { fontSize: 13 } }}>A última coleta terminou com erro{coleta.status.contas ? `: ${Object.entries(coleta.status.contas).map(([u, c]) => `${nomeConta(u)} — ${(c as { erro?: string }).erro || 'ok'}`).join(' · ')}` : ''}. Os números abaixo são do último retrato bom.</Alert> : null}
              </CardContent>
            </Card>

            {resumo?.vazio ? (
              <Alert severity="info" action={<Button color="inherit" size="small" onClick={() => void atualizar()} disabled={rodando}>Atualizar</Button>}>
                {resumo.motivo || 'Nenhuma coleta ainda.'}
              </Alert>
            ) : tot ? (
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 6, sm: 4, md: 3, lg: 1.5 }}><KpiCard label="Gasto em Ads" value={brl(tot.cost)} hint={`antes ${brl(tot.anterior.cost)}`} deltaPct={tot.delta.cost ?? null} /></Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3, lg: 1.5 }}><KpiCard label="Venda atribuída" value={brl(tot.total_amount)} hint={`direta ${brl(tot.direct_amount)} · indireta ${brl(tot.indirect_amount)}`} deltaPct={tot.delta.total_amount ?? null} /></Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3, lg: 1.5 }}><KpiCard label="ACOS" value={pct(tot.acos)} badge={metaAcos != null ? `meta ${pct(metaAcos)}${tot.acos != null ? (tot.acos <= metaAcos ? ' · na meta' : ' · acima') : ''}` : null} hint={`antes ${pct(tot.anterior.acos)}`} deltaPct={tot.delta.acos ?? null} /></Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3, lg: 1.5 }}><KpiCard label="ROAS" value={tot.roas == null ? '—' : `${tot.roas}×`} hint={`antes ${tot.anterior.roas == null ? '—' : `${tot.anterior.roas}×`}`} deltaPct={tot.delta.roas ?? null} /></Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3, lg: 1.5 }}><KpiCard label="Conversão das campanhas" value={pct(tot.conversao, 2)} hint="vendas atribuídas ÷ cliques" deltaPct={tot.delta.conversao ?? null} /></Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3, lg: 1.5 }}><KpiCard label="Cliques" value={inteiro(tot.clicks)} hint={`${inteiro(tot.prints)} impressões`} deltaPct={tot.delta.clicks ?? null} /></Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3, lg: 1.5 }}><KpiCard label="CPC" value={brl(tot.cpc)} hint={`antes ${brl(tot.anterior.cpc)}`} deltaPct={tot.delta.cpc ?? null} /></Grid>
                <Grid size={{ xs: 6, sm: 4, md: 3, lg: 1.5 }}>
                  <KpiCard label="TACOS" value={pct(resumo?.tacos?.total ?? null)} badge={metas.tacos_alvo != null ? `meta ${pct(metas.tacos_alvo)}${resumo?.tacos?.total != null ? (resumo.tacos.total <= metas.tacos_alvo ? ' · na meta' : ' · acima') : ''} · base margens` : 'base margens'} valueTone={resumo?.tacos?.total == null ? 'caveat' : 'default'}
                    hint={resumo?.tacos?.faturamento ? `gasto ÷ faturamento real ${brl(Number(resumo.tacos.faturamento.total))}` : 'sem faturamento no período'} />
                </Grid>
              </Grid>
            ) : null}

            {resumo && !resumo.vazio && Object.keys(resumo.contas).length > 1 ? (
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {Object.entries(resumo.contas).map(([u, c]) => (c.dias_com_dado === 0 ? `${nomeConta(u)}: sem dado no período` : `${nomeConta(u)}: gasto ${brl(c.cost)} · ACOS ${pct(c.acos)} · TACOS ${pct(resumo.tacos?.[u] ?? null)}`)).join('   ·   ')}
              </Typography>
            ) : null}

            {resumo && !resumo.vazio ? (
              <Card>
                <CardContent>
                  <Tabs value={aba} onChange={(_, v: Aba) => setAba(v)} variant="scrollable" allowScrollButtonsMobile sx={{ mb: 2 }}>
                    {ABAS.map(([k, label]) => <Tab key={k} value={k} label={k === 'campanhas' && campanhas ? `${label} (${campanhas.length})` : label} />)}
                  </Tabs>
                  {abaLoading ? <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>carregando…</Typography> : null}
                  <Box>{conteudoAba()}</Box>
                </CardContent>
              </Card>
            ) : null}
          </>
        )}
      </Stack>
    </DomainPageShell>
  )
}
